import {
  IonCard,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonIcon,
  IonLabel,
} from '@ionic/react';
import { checkmarkCircle, warning } from 'ionicons/icons';
import { ReactElement } from 'react';
import { Machine } from '../../../api/graphql/generated';
import { useMachineConnected } from '../../../api/hooks/machine.hooks';
import i18n from '../../../i18n';
import { useBeaconRouter } from '../../../route/route.hooks';
import { DateField } from '../../ui/date-field';
import './card.css';

interface MachineInfoCardProps {
  machine?: Machine;
}

const MachineInfoCard = (props: MachineInfoCardProps): ReactElement => {
  const { machine } = props;

  const isConnected = useMachineConnected({ machine });

  const { builder } = useBeaconRouter();

  return (
    <IonCard
      className="machine-card-container"
      routerLink={
        machine
          ? builder.getMachineDetailsRoute(machine.teamId, machine.id)
          : undefined
      }
    >
      <IonCardHeader>
        <IonCardTitle>{i18n.t('common:machine_info')}</IonCardTitle>
        {machine?.demo === true && (
          <IonCardSubtitle className="no-text-transform">
            {i18n.t('common:machine_demo')}
          </IonCardSubtitle>
        )}
      </IonCardHeader>
      <div className="machine-card-info-grid">
        <IonLabel className="machine-card-info-grid-label" color="medium">
          <p>{i18n.t('common:machine_online')}</p>
        </IonLabel>
        {isConnected === true ? (
          <IonIcon
            className="machine-card-info-grid-value"
            icon={checkmarkCircle}
            color="success"
          />
        ) : (
          <IonIcon
            className="machine-card-info-grid-value"
            icon={warning}
            color="danger"
          />
        )}

        <IonLabel className="machine-card-info-grid-label" color="medium">
          <p>{i18n.t('common:last_activity')}</p>
        </IonLabel>
        <IonLabel className="machine-card-info-grid-value" color="medium">
          <p>
            <DateField date={machine?.state?.updatedAt} />
          </p>
        </IonLabel>

        <IonLabel className="machine-card-info-grid-label" color="medium">
          <p>{i18n.t('common:loaded_program')}</p>
        </IonLabel>
        <IonLabel className="machine-card-info-grid-value" color="medium">
          <p>
            {machine?.state?.runtimeInfo?.programName ?? `<unknown program>`}
          </p>
        </IonLabel>

        <IonLabel className="machine-card-info-grid-label" color="medium">
          <p>{i18n.t('common:machine_serial_number')}</p>
        </IonLabel>
        <IonLabel className="machine-card-info-grid-value" color="medium">
          <p>
            {machine?.state?.runtimeInfo?.serialNumber ??
              i18n.t('common:unknown')}
          </p>
        </IonLabel>

        <IonLabel className="machine-card-info-grid-label" color="medium">
          <p>{i18n.t('common:machine_polyscope')}</p>
        </IonLabel>
        <IonLabel className="machine-card-info-grid-value" color="medium">
          <p>{machine?.state?.runtimeInfo?.softwareVersion || 'n/a'}</p>
        </IonLabel>

        <IonLabel className="machine-card-info-grid-label" color="medium">
          <p>{i18n.t('common:machine_firmware')}</p>
        </IonLabel>
        <IonLabel className="machine-card-info-grid-value" color="medium">
          <p>{machine?.state?.runtimeInfo?.firmwareVersion || 'n/a'}</p>
        </IonLabel>

        <IonLabel className="machine-card-info-grid-label" color="medium">
          <p>{i18n.t('common:machine_urcap')}</p>
        </IonLabel>
        <IonLabel className="machine-card-info-grid-value" color="medium">
          <p>{machine?.state?.runtimeInfo?.urcapVersion || 'n/a'}</p>
        </IonLabel>

        <IonLabel className="machine-card-info-grid-label" color="medium">
          <p>{i18n.t('common:machine_provisioning_status')}</p>
        </IonLabel>
        <IonLabel
          className="machine-card-info-grid-value"
          color={machine?.active ? 'medium' : 'danger'}
        >
          <p>
            {machine?.active
              ? i18n.t('common:active')
              : i18n.t('common:inactive')}
          </p>
        </IonLabel>
      </div>
    </IonCard>
  );
};

export { MachineInfoCard };
